import { useState, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { Play, ListPlus, ListEnd, Plus, ChevronRight, X, Check } from 'lucide-react';
import { useAudio } from '@/context/AudioContext';
import { useAuth } from '@/context/AuthContext';
import type { AudioTrack, Playlist } from '@/types/audio';
import { subscribeToPlaylists, addTracksToPlaylist } from '@/services/playlists';

interface TrackActionMenuProps {
  track: AudioTrack;
  isOpen: boolean;
  onClose: () => void;
  sourcePosition?: DOMRect;
}

/**
 * Action menu for track playback options
 * Shows as a bottom sheet drawer for reliable positioning
 * Options: Play Now, Play Next, Add to Queue, Add to Playlist
 */
export const TrackActionMenu = memo(function TrackActionMenu({
  track,
  isOpen,
  onClose,
  sourcePosition,
}: TrackActionMenuProps) {
  const { addToQueue } = useAudio();
  const { authState } = useAuth();
  const [showPlaylistPicker, setShowPlaylistPicker] = useState(false);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [addedToPlaylistId, setAddedToPlaylistId] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // Get user ID from auth state
  const userId = authState.status === 'authenticated' ? authState.profile.uid : null;

  // Handle open/close animation — double-rAF ensures the browser has painted
  // the initial off-screen state before transitioning, which Safari requires.
  useEffect(() => {
    if (isOpen) {
      let cancelled = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setIsAnimating(true);
        });
      });
      return () => {
        cancelled = true;
      };
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  // Load user's playlists
  useEffect(() => {
    if (!userId || !isOpen) return;

    const unsubscribe = subscribeToPlaylists(userId, (loadedPlaylists) => {
      setPlaylists(loadedPlaylists);
    });

    return unsubscribe;
  }, [userId, isOpen]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showPlaylistPicker) {
          setShowPlaylistPicker(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, showPlaylistPicker]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setShowPlaylistPicker(false);
      setAddedToPlaylistId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePlayNow = () => {
    addToQueue(track, 'now', sourcePosition);
    onClose();
  };

  const handlePlayNext = () => {
    addToQueue(track, 'next', sourcePosition);
    onClose();
  };

  const handleAddToQueue = () => {
    addToQueue(track, 'end', sourcePosition);
    onClose();
  };

  const handleAddToPlaylist = async (playlistId: string) => {
    if (!userId) return;

    try {
      await addTracksToPlaylist(userId, playlistId, [track]);
      setAddedToPlaylistId(playlistId);
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('Error adding track to playlist:', error);
    }
  };

  // Portal to document.body so `fixed` positioning isn't broken by
  // ancestor CSS transforms (e.g. hover:-translate-y on cards).
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[999] bg-black/30 transition-opacity duration-200 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Bottom Sheet */}
      <div
        className={`fixed inset-x-0 bottom-0 z-[1000] transition-transform duration-300 ease-out ${
          isAnimating ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="bg-white rounded-t-2xl shadow-xl max-w-lg mx-auto pb-safe">
          {/* Drag handle indicator */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-slate-300 rounded-full" />
          </div>

          {!showPlaylistPicker ? (
            <>
              {/* Track info header */}
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-base font-semibold text-slate-800 truncate">{track.title}</p>
                <p className="text-sm text-slate-500 truncate">{track.source}</p>
              </div>

              {/* Action options */}
              <div className="py-2">
                <button
                  onClick={handlePlayNow}
                  className="w-full flex items-center gap-4 px-4 py-3 active:bg-[#f8e8f8] transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-[#8e4585] flex items-center justify-center">
                    <Play size={18} className="text-white ml-0.5" />
                  </div>
                  <div className="text-left">
                    <p className="text-base font-medium text-slate-800">Play Now</p>
                    <p className="text-sm text-slate-500">Start playing immediately</p>
                  </div>
                </button>

                <button
                  onClick={handlePlayNext}
                  className="w-full flex items-center gap-4 px-4 py-3 active:bg-[#f8e8f8] transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <ListPlus size={20} className="text-slate-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-base font-medium text-slate-800">Play Next</p>
                    <p className="text-sm text-slate-500">Add after current track</p>
                  </div>
                </button>

                <button
                  onClick={handleAddToQueue}
                  className="w-full flex items-center gap-4 px-4 py-3 active:bg-[#f8e8f8] transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <ListEnd size={20} className="text-slate-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-base font-medium text-slate-800">Add to Queue</p>
                    <p className="text-sm text-slate-500">Add to end of queue</p>
                  </div>
                </button>

                <div className="my-2 mx-4 border-t border-slate-100" />

                <button
                  onClick={() => setShowPlaylistPicker(true)}
                  className="w-full flex items-center gap-4 px-4 py-3 active:bg-[#f8e8f8] transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <Plus size={20} className="text-slate-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-base font-medium text-slate-800">Add to Playlist</p>
                    <p className="text-sm text-slate-500">Save to a playlist</p>
                  </div>
                  <ChevronRight size={20} className="text-slate-400" />
                </button>
              </div>

              {/* Cancel button */}
              <div className="px-4 pb-4">
                <button
                  onClick={onClose}
                  className="w-full py-3 text-base font-medium text-slate-600 bg-slate-100 rounded-xl active:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Playlist picker header */}
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <button
                  onClick={() => setShowPlaylistPicker(false)}
                  className="p-2 -ml-2 active:bg-slate-100 rounded-lg transition-colors"
                >
                  <ChevronRight size={20} className="text-slate-600 rotate-180" />
                </button>
                <p className="text-base font-semibold text-slate-800">Add to Playlist</p>
                <button
                  onClick={onClose}
                  className="p-2 -mr-2 active:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-slate-600" />
                </button>
              </div>

              {/* Playlist list */}
              <div className="py-2 max-h-[280px] overflow-y-auto">
                {playlists.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <p className="text-base text-slate-500">No playlists yet</p>
                    <p className="text-sm text-slate-400 mt-1">Create one from the player</p>
                  </div>
                ) : (
                  playlists.map((playlist) => (
                    <button
                      key={playlist.id}
                      onClick={() => handleAddToPlaylist(playlist.id)}
                      disabled={addedToPlaylistId === playlist.id}
                      className="w-full flex items-center gap-4 px-4 py-3 active:bg-[#f8e8f8] transition-colors disabled:opacity-50"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#8e4585] to-[#63345e] flex items-center justify-center text-white text-sm font-bold">
                        {playlist.trackCount}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-base font-medium text-slate-800 truncate">
                          {playlist.name}
                        </p>
                        <p className="text-sm text-slate-500">
                          {playlist.trackCount} track{playlist.trackCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                      {addedToPlaylistId === playlist.id && (
                        <Check size={20} className="text-green-500" />
                      )}
                    </button>
                  ))
                )}
              </div>

              {/* Cancel button */}
              <div className="px-4 pb-4">
                <button
                  onClick={onClose}
                  className="w-full py-3 text-base font-medium text-slate-600 bg-slate-100 rounded-xl active:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
});

export default TrackActionMenu;
